import { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Send, CheckCircle2, Loader2, LogOut, Plus, ArrowLeft,
  TrendingUp, TrendingDown, RefreshCw, UserCheck,
  Edit2, Trash2, X, Save, Search, Repeat, Zap, AlertTriangle,
  MessageSquare, ChevronDown, ChevronUp,
} from 'lucide-react'
import CommentDialog from '../components/CommentDialog'
import { useAuthStore } from '../stores/auth'
import { useCashflowStore } from '../stores/cashflow'
import { useToastStore } from '../stores/toast'
import * as api from '../api'
import { formatAmount, formatShortDate, formatTimestamp } from '../utils/format'
import { today as todayStr, daysAgo as daysAgoStr } from '../utils/dates'
import { dueDateLabel, DUE_DATE_COLUMN_LABEL } from '../utils/labels'
import { ZaimuKun } from '../components/mascot/zaimu-kun'
// 2026-05-14 リファクタ: 機能別に features/input/ 配下へ分離
import type { LogEntry } from '../features/input/types'
import DuplicateCheckDialog from '../features/input/components/DuplicateCheckDialog'
import InputHistoryFilters from '../features/input/components/InputHistoryFilters'
// R-9 (2026-05-24): 登録履歴セクションを分離
import InputHistoryTable from '../features/input/components/InputHistoryTable'

export default function InputPage() {
  const { user, logout } = useAuthStore()
  const { categories, departments, items, loadAll } = useCashflowStore()
  const toast = useToastStore()

  // 独立モード判定: allowedPages が ['input'] のみのユーザー（サイドバー・TopBarなし）
  const isStandalone = user?.role !== 'admin'
    && Array.isArray(user?.allowedPages)
    && user!.allowedPages!.length === 1
    && user!.allowedPages![0] === 'input'

  const [type, setType] = useState<'income' | 'expense'>('expense')
  /** 取引種別（定期 or 単発） */
  const [isRecurring, setIsRecurring] = useState<boolean>(false)
  /** シークレット取引フラグ（権限ある人のみ編集可） */
  const [isSecret, setIsSecret] = useState<boolean>(false)
  const [categoryId, setCategoryId] = useState<string>('')
  const [vendorId, setVendorId] = useState<string>('')
  /** 請求日（資金繰り表のセル位置を決める） */
  const [date, setDate] = useState<string>(todayStr())
  /** 支払期限（任意、情報用） */
  const [dueDate, setDueDate] = useState<string>('')
  /** 対象月（YYYY-MM、任意）: 「何月分の請求書か」 */
  const [targetMonth, setTargetMonth] = useState<string>('')
  /** 部門ID（任意、最大4階層） */
  const [departmentId, setDepartmentId] = useState<string>('')
  /** 品目ID（任意、フラットマスタ） */
  const [itemId, setItemId] = useState<string>('')
  const [amount, setAmount] = useState<string>('')
  const [note, setNote] = useState<string>('')
  const [saving, setSaving] = useState(false)

  /** 編集中セルの元キー（"vendorId_date"）。空なら新規登録モード */
  const [editingKey, setEditingKey] = useState<string>('')
  /** 編集中に表示する取引先名（バナー用） */
  const [editingLabel, setEditingLabel] = useState<string>('')
  /** 削除中のキー（ダブルクリック防止） */
  const [deletingKey, setDeletingKey] = useState<string>('')

  // 新規取引先
  const [addingVendor, setAddingVendor] = useState(false)
  const [newVendorName, setNewVendorName] = useState('')
  const [creatingVendor, setCreatingVendor] = useState(false)
  // 新規カテゴリ
  const [addingCategory, setAddingCategory] = useState(false)
  const [newCategoryName, setNewCategoryName] = useState('')
  const [creatingCategory, setCreatingCategory] = useState(false)
  // 新規部門
  const [addingDepartment, setAddingDepartment] = useState(false)
  const [newDepartmentName, setNewDepartmentName] = useState('')
  const [creatingDepartment, setCreatingDepartment] = useState(false)
  // 新規品目
  const [addingItem, setAddingItem] = useState(false)
  const [newItemName, setNewItemName] = useState('')
  const [creatingItem, setCreatingItem] = useState(false)
  // フォーム折りたたみ状態（localStorage に永続化）
  const [formCollapsed, setFormCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    return localStorage.getItem('inputPage.formCollapsed') === '1'
  })
  useEffect(() => {
    if (typeof window === 'undefined') return
    localStorage.setItem('inputPage.formCollapsed', formCollapsed ? '1' : '0')
  }, [formCollapsed])

  // 過去N日分のセル一覧（下部の履歴表示用）
  const [recentCells, setRecentCells] = useState<Record<string, number>>({})
  const [recentMeta, setRecentMeta] = useState<Record<string, any>>({})
  const [recentUpdatedAt, setRecentUpdatedAt] = useState<Record<string, string>>({})
  const [recentUpdatedBy, setRecentUpdatedBy] = useState<Record<string, { id: string; name: string }>>({})
  // 新形式（sub_id別エントリー配列）
  const [recentCellEntries, setRecentCellEntries] = useState<Array<{
    vendorId: string; date: string; subId: number; amount: number;
    updatedAt: string; updatedBy: { id: string; name: string } | null;
  }>>([])
  const [recentCellMetaEntries, setRecentCellMetaEntries] = useState<Array<any>>([])
  const [loadingRecent, setLoadingRecent] = useState(true)

  // コメント件数マップ: key = "vendorId_date_subId" → { total, unread }
  const [commentCounts, setCommentCounts] = useState<Record<string, { total: number; unread: number }>>({})
  // コメントダイアログ対象
  const [commentTarget, setCommentTarget] = useState<{
    vendorId: string; date: string; subId: number; vendorName: string; amount: number;
  } | null>(null)

  // 検索・フィルタ・期間・ソート
  const [searchQuery, setSearchQuery] = useState('')
  const [typeFilter, setTypeFilter] = useState<'all' | 'income' | 'expense'>('all')
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'confirmed'>('all')
  const [recurringFilter, setRecurringFilter] = useState<'all' | 'recurring' | 'onetime'>('all')
  const [periodDays, setPeriodDays] = useState<number>(365)
  const [sortKey, setSortKey] = useState<'date' | 'amount' | 'vendor' | 'updatedAt'>('date')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  useEffect(() => { loadAll() }, [loadAll])

  // 取引履歴の取得（期間はperiodDaysに応じて動的）
  const loadRecent = useCallback(async () => {
    setLoadingRecent(true)
    try {
      const from = daysAgoStr(periodDays)
      const to = todayStr()
      const res = await api.cells.list(from, to, { mine: true }) as any
      setRecentCells(res.cells || {})
      setRecentMeta(res.cellMeta || {})
      setRecentUpdatedAt(res.cellUpdatedAt || {})
      setRecentUpdatedBy(res.cellUpdatedBy || {})
      setRecentCellEntries(res.cellEntries || [])
      setRecentCellMetaEntries(res.cellMetaEntries || [])
    } catch (err) {
      console.error('履歴取得失敗:', err)
    } finally {
      setLoadingRecent(false)
    }
  }, [periodDays])

  useEffect(() => { loadRecent() }, [loadRecent])

  // コメント件数マップの取得
  const loadCommentCounts = useCallback(async () => {
    try {
      const res = await api.comments.counts()
      setCommentCounts(res.counts || {})
    } catch {
      /* 権限無し時は無視 */
    }
  }, [])
  useEffect(() => { loadCommentCounts() }, [loadCommentCounts])

  // サイドピークの開閉アニメーション制御
  // editingKey セット直後に1tick 遅れて panelOpen を true にすることで、transform transition がかかる
  const [panelOpen, setPanelOpen] = useState(false)
  useEffect(() => {
    if (editingKey) {
      const t = setTimeout(() => setPanelOpen(true), 20)
      return () => clearTimeout(t)
    }
    setPanelOpen(false)
  }, [editingKey])

  // Esc キーで編集モードをキャンセル（サイドピーク閉じる）
  useEffect(() => {
    if (!editingKey) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // 入力中の input/textarea にフォーカスがある場合はそちらのEscが優先される仕様を尊重
        const tag = (e.target as HTMLElement)?.tagName
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
        exitEdit()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingKey])

  // 品目のマップ（id→name）
  const itemMap = useMemo(() => {
    const map = new Map<string, { id: string; name: string; color: string }>()
    for (const i of items || []) {
      map.set(i.id, { id: i.id, name: i.name, color: i.color })
    }
    return map
  }, [items])

  const sortedItems = useMemo(() => {
    return [...(items || [])].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0) || a.name.localeCompare(b.name, 'ja'))
  }, [items])

  // 部門のマップと階層パス計算
  const departmentMap = useMemo(() => {
    const map = new Map<string, { id: string; name: string; parentId: string | null; level: number }>()
    for (const d of departments || []) {
      map.set(d.id, { id: d.id, name: d.name, parentId: d.parentId || null, level: d.level || 1 })
    }
    return map
  }, [departments])

  // 指定ID から階層パスを構築（"本社 > 営業部 > 関東"）
  const getDepartmentPath = useCallback((id: string): string => {
    if (!id) return ''
    const parts: string[] = []
    let current = departmentMap.get(id)
    const seen = new Set<string>() // 循環防止
    while (current && !seen.has(current.id)) {
      seen.add(current.id)
      parts.unshift(current.name)
      if (!current.parentId) break
      current = departmentMap.get(current.parentId)
    }
    return parts.join(' > ')
  }, [departmentMap])

  // セレクタ用の部門リスト（階層順でインデント付き）
  const departmentOptions = useMemo(() => {
    const sorted = [...(departments || [])]
    // 階層 → sort_order → name の順
    sorted.sort((a, b) => {
      const pathA = getDepartmentPath(a.id)
      const pathB = getDepartmentPath(b.id)
      return pathA.localeCompare(pathB, 'ja')
    })
    return sorted.map((d) => ({
      id: d.id,
      // インデント用の空白（全角 × (level-1)）
      label: '　'.repeat(Math.max(0, (d.level || 1) - 1)) + d.name,
      path: getDepartmentPath(d.id),
      level: d.level || 1,
    }))
  }, [departments, getDepartmentPath])

  // カテゴリ・取引先情報のマップ
  const vendorInfo = useMemo(() => {
    const map: Record<string, { name: string; categoryId: string; categoryName: string; type: 'income' | 'expense' }> = {}
    for (const cat of categories.income || []) {
      for (const v of cat.vendors || []) map[v.id] = { name: v.name, categoryId: cat.id, categoryName: cat.name, type: 'income' }
    }
    for (const cat of categories.expense || []) {
      for (const v of cat.vendors || []) map[v.id] = { name: v.name, categoryId: cat.id, categoryName: cat.name, type: 'expense' }
    }
    return map
  }, [categories])

  // 履歴一覧: 新形式の cellEntries をベースに構築（sub_id別の個別行）
  // cellMetaEntries を (vendorId, date, subId) で lookup
  const logEntries = useMemo<LogEntry[]>(() => {
    const metaMap = new Map<string, any>()
    for (const m of recentCellMetaEntries || []) {
      metaMap.set(`${m.vendorId}_${m.date}_${m.subId}`, m)
    }
    const entries: LogEntry[] = []
    for (const c of recentCellEntries || []) {
      // シークレットマスク取引は amount=0 でサーバから来るが、エントリは作る（マスク表示のため）
      const isMasked = !!(c as any).isSecretMasked
      if (!c.amount && !isMasked) continue
      const info = vendorInfo[c.vendorId]
      if (!info) continue
      const key = `${c.vendorId}_${c.date}_${c.subId}`
      const meta = metaMap.get(key) || {}
      const updatedByName =
        c.updatedBy?.name ||
        meta.updatedBy?.name ||
        ''
      entries.push({
        key,
        date: c.date,
        subId: c.subId,
        dueDate: meta.dueDate || '',
        targetMonth: meta.targetMonth || '',
        updatedByName,
        departmentId: meta.departmentId || '',
        departmentPath: meta.departmentId ? getDepartmentPath(meta.departmentId) : '',
        itemId: meta.itemId || '',
        itemName: meta.itemId ? (itemMap.get(meta.itemId)?.name || '') : '',
        vendorId: c.vendorId,
        vendorName: info.name,
        categoryId: info.categoryId,
        categoryName: info.categoryName,
        type: info.type,
        isRecurring: !!meta.isRecurring,
        amount: c.amount,
        note: meta.note || '',
        updatedAt: c.updatedAt || meta.updatedAt || '',
        confirmedByName: meta.confirmedBy?.name || '',
        confirmedAt: meta.confirmedAt || '',
        isSecret: !!(meta as any).isSecret,
        isSecretMasked: isMasked,
      })
    }
    entries.sort((a, b) => b.date.localeCompare(a.date) || b.key.localeCompare(a.key))
    return entries
  }, [recentCellEntries, recentCellMetaEntries, vendorInfo, getDepartmentPath, itemMap])

  // 互換のため recent{Cells,Meta,UpdatedAt,UpdatedBy} は state には残すが、
  // 直接参照しない（上記 logEntries はエントリー配列から生成）。
  void recentCells; void recentMeta; void recentUpdatedAt; void recentUpdatedBy

  const todayLogCount = logEntries.filter((e) => e.date === todayStr()).length

  // 検索・フィルタ・ソート適用後の一覧
  const filteredLogEntries = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    // 金額検索用: クエリからカンマ・¥・円・空白を除去して数字部分を抽出
    // 例: "1,762,474" / "¥1762474" / "1762474円" → "1762474"
    const numQuery = q.replace(/[¥円,\s]/g, '')
    const isNumericQuery = numQuery !== '' && /^\d+$/.test(numQuery)
    let list = logEntries.filter((e) => {
      // 収支フィルタ
      if (typeFilter !== 'all' && e.type !== typeFilter) return false
      // 状態フィルタ
      if (statusFilter === 'confirmed' && !e.confirmedByName) return false
      if (statusFilter === 'pending' && e.confirmedByName) return false
      // 定期/単発フィルタ
      if (recurringFilter === 'recurring' && !e.isRecurring) return false
      if (recurringFilter === 'onetime' && e.isRecurring) return false
      // 検索ワード（取引先・カテゴリ・メモ・日付・金額）
      if (!q) return true
      return (
        e.vendorName.toLowerCase().includes(q) ||
        e.categoryName.toLowerCase().includes(q) ||
        e.note.toLowerCase().includes(q) ||
        e.date.includes(q) ||
        (e.dueDate || '').includes(q) ||
        (e.targetMonth || '').includes(q) ||
        (e.updatedByName || '').toLowerCase().includes(q) ||
        (e.departmentPath || '').toLowerCase().includes(q) ||
        (e.itemName || '').toLowerCase().includes(q) ||
        // 金額検索: 数字のみのクエリの場合、金額に完全一致するかチェック
        // 例: クエリ "1762474" は 1,762,474 にヒット。"1762" は何もヒットしない
        // （部分一致だとノイズが多すぎるため、上田さん指示で完全一致に統一 2026-05-10）
        (isNumericQuery && String(e.amount) === numQuery)
      )
    })
    // ソート
    list = [...list].sort((a, b) => {
      let cmp = 0
      switch (sortKey) {
        case 'date': cmp = a.date.localeCompare(b.date); break
        case 'amount': cmp = a.amount - b.amount; break
        case 'vendor': cmp = a.vendorName.localeCompare(b.vendorName, 'ja'); break
        case 'updatedAt': cmp = (a.updatedAt || '').localeCompare(b.updatedAt || ''); break
      }
      return sortDir === 'asc' ? cmp : -cmp
    })
    return list
  }, [logEntries, searchQuery, typeFilter, statusFilter, recurringFilter, sortKey, sortDir])

  // 集計（フィルタ後の合計・件数）
  const aggregate = useMemo(() => {
    const income = filteredLogEntries.filter((e) => e.type === 'income').reduce((s, e) => s + e.amount, 0)
    const expense = filteredLogEntries.filter((e) => e.type === 'expense').reduce((s, e) => s + e.amount, 0)
    return { income, expense, net: income - expense, count: filteredLogEntries.length }
  }, [filteredLogEntries])

  // 重複チェックダイアログの開閉。実際の計算ロジックと UI は
  // features/input/components/DuplicateCheckDialog.tsx に分離（2026-05-14）。
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false)

  const toggleSort = (key: typeof sortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir(key === 'vendor' ? 'asc' : 'desc')
    }
  }

  const sortIcon = (key: typeof sortKey) => {
    if (sortKey !== key) return ''
    return sortDir === 'asc' ? ' ↑' : ' ↓'
  }

  // 確認ボタンの処理（履歴行ごと）
  const [confirmingKey, setConfirmingKey] = useState<string>('')
  const handleConfirm = async (vendorId: string, dateStr: string) => {
    const key = `${vendorId}_${dateStr}`
    setConfirmingKey(key)
    try {
      await api.cells.confirm(vendorId, dateStr)
      toast.success('確認済みにしました')
      await loadRecent()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '確認に失敗しました')
    } finally {
      setConfirmingKey('')
    }
  }

  const availableCategories = useMemo(() => {
    return type === 'income' ? (categories.income || []) : (categories.expense || [])
  }, [categories, type])

  const availableVendors = useMemo(() => {
    const cat = availableCategories.find((c) => c.id === categoryId)
    return cat?.vendors || []
  }, [availableCategories, categoryId])

  useEffect(() => {
    if (availableCategories.length > 0 && !availableCategories.find((c) => c.id === categoryId)) {
      setCategoryId(availableCategories[0].id)
      setVendorId('')
    }
  }, [availableCategories, categoryId])

  // カテゴリ変更時の vendorId リセットは onChange ハンドラで直接行う
  // （useEffect で categoryId 依存にすると、編集モードで categoryId と vendorId を同時に set する際に競合するため）

  useEffect(() => {
    setAddingCategory(false)
    setNewCategoryName('')
  }, [type])

  const handleCreateCategory = async () => {
    const trimmed = newCategoryName.trim()
    if (!trimmed) { toast.error('カテゴリ名を入力してください'); return }
    setCreatingCategory(true)
    try {
      const res = await api.categories.create({ name: trimmed, type }) as any
      toast.success(`カテゴリ「${trimmed}」を作成しました`, 1500)
      await loadAll()
      if (res?.id) setCategoryId(res.id)
      setAddingCategory(false)
      setNewCategoryName('')
    } catch (err) {
      toast.error(`カテゴリ作成失敗: ${err instanceof Error ? err.message : ''}`)
    } finally {
      setCreatingCategory(false)
    }
  }

  const handleCreateVendor = async () => {
    const trimmed = newVendorName.trim()
    if (!trimmed) { toast.error('取引先名を入力してください'); return }
    if (!categoryId) { toast.error('カテゴリを選択してください'); return }
    setCreatingVendor(true)
    try {
      const res = await api.vendors.create({ name: trimmed, categoryId }) as any
      toast.success(`取引先「${trimmed}」を作成しました`, 1500)
      await loadAll()
      if (res?.id) setVendorId(res.id)
      setAddingVendor(false)
      setNewVendorName('')
    } catch (err) {
      toast.error(`取引先作成失敗: ${err instanceof Error ? err.message : ''}`)
    } finally {
      setCreatingVendor(false)
    }
  }

  const handleCreateDepartment = async () => {
    const trimmed = newDepartmentName.trim()
    if (!trimmed) { toast.error('部門名を入力してください'); return }
    setCreatingDepartment(true)
    try {
      // 最上位階層に作成（必要なら後でマスタ管理画面で階層化可能）
      const res = await api.departments.create({ name: trimmed, parentId: null }) as any
      toast.success(`部門「${trimmed}」を作成しました`, 1500)
      await loadAll()
      if (res?.id) setDepartmentId(res.id)
      setAddingDepartment(false)
      setNewDepartmentName('')
    } catch (err) {
      toast.error(`部門作成失敗: ${err instanceof Error ? err.message : ''}`)
    } finally {
      setCreatingDepartment(false)
    }
  }

  const handleCreateItem = async () => {
    const trimmed = newItemName.trim()
    if (!trimmed) { toast.error('品目名を入力してください'); return }
    setCreatingItem(true)
    try {
      const res = await api.items.create({ name: trimmed }) as any
      toast.success(`品目「${trimmed}」を作成しました`, 1500)
      await loadAll()
      if (res?.id) setItemId(res.id)
      setAddingItem(false)
      setNewItemName('')
    } catch (err) {
      toast.error(`品目作成失敗: ${err instanceof Error ? err.message : ''}`)
    } finally {
      setCreatingItem(false)
    }
  }

  const resetForm = () => {
    setAmount('')
    setNote('')
  }

  /** 編集モードを終了してフォームを新規登録状態に戻す */
  const exitEdit = () => {
    setEditingKey('')
    setEditingLabel('')
    setAmount('')
    setNote('')
    setDate(todayStr())
    setDueDate('')
    setTargetMonth('')
    setDepartmentId('')
    setItemId('')
    setIsRecurring(false)
    setIsSecret(false)
    // type/categoryId/vendorId はユーザーの選択を保持（連続入力を優先）
  }

  /** 行の「編集」クリック: 上部フォームに値を反映 */
  const handleStartEdit = (r: LogEntry) => {
    setEditingKey(r.key)
    setEditingLabel(`${r.date} ${r.vendorName}`)
    setType(r.type)
    setIsRecurring(r.isRecurring)
    setIsSecret(r.isSecret)
    setCategoryId(r.categoryId)
    setVendorId(r.vendorId)
    setDate(r.date)
    setDueDate(r.dueDate || '')
    setTargetMonth(r.targetMonth || '')
    setDepartmentId(r.departmentId || '')
    setItemId(r.itemId || '')
    setAmount(String(r.amount))
    setNote(r.note)
    setAddingVendor(false)
    setAddingCategory(false)
    // 編集する行が画面の中央に見えるようスクロール（フォームは sticky で上部固定）
    if (typeof window !== 'undefined') {
      setTimeout(() => {
        const el = document.getElementById(`log-row-${r.key.replace(/[^a-zA-Z0-9_-]/g, '_')}`)
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 50)
    }
  }

  /** 行の「削除」クリック: 確認の上、金額を0にしてソフトデリート */
  const handleDelete = async (r: LogEntry) => {
    const confirmedSuffix = r.confirmedByName ? '\n\n※ この取引は確認済みです。削除するとダブルチェック記録も失われます。' : ''
    const msg = `この取引を削除しますか？\n\n${r.date} ${r.vendorName}\n${r.type === 'expense' ? '−' : ''}¥${r.amount.toLocaleString()}\n\n（30日間はゴミ箱から復元可能）${confirmedSuffix}`
    if (!window.confirm(msg)) return
    setDeletingKey(r.key)
    try {
      await api.cells.save({ cells: { [r.key]: 0 } })
      toast.success('削除しました', 2000)
      // 編集中の行を削除した場合は編集モードも解除
      if (editingKey === r.key) exitEdit()
      await loadRecent()
    } catch (err) {
      toast.error(`削除失敗: ${err instanceof Error ? err.message : ''}`)
    } finally {
      setDeletingKey('')
    }
  }

  const handleSave = async () => {
    const amountNum = Number(amount.replace(/,/g, '').trim())
    if (!amount || isNaN(amountNum) || amountNum <= 0) {
      toast.error('金額を正しく入力してください（1円以上）')
      return
    }
    if (!date) { toast.error('請求日を選択してください'); return }
    // 期日（収入=入金期日 / 支出=支払期限）が請求日より前になっていないかチェック（逆転防止）
    if (dueDate && dueDate < date) {
      toast.error(`${dueDateLabel(type)}は請求日以降の日付にしてください`)
      return
    }

    let targetVendorId = vendorId
    let targetVendorName = availableVendors.find((v) => v.id === vendorId)?.name || ''

    if (addingVendor) {
      if (!newVendorName.trim()) { toast.error('取引先名を入力してください'); return }
      if (!categoryId) { toast.error('カテゴリを選択してください'); return }
      setSaving(true)
      try {
        const res = await api.vendors.create({ name: newVendorName.trim(), categoryId }) as any
        targetVendorId = res.id
        targetVendorName = newVendorName.trim()
        toast.success(`取引先「${targetVendorName}」を作成`, 1500)
      } catch (err) {
        toast.error(`取引先作成失敗: ${err instanceof Error ? err.message : ''}`)
        setSaving(false)
        return
      }
    } else if (!vendorId) {
      toast.error('取引先を選択してください')
      return
    }

    // === 新規登録時、同日同取引先の既存セルがある場合の扱い ===
    // 「別件として追加」するか「既存を上書き」するかをユーザーに確認
    let subIdSuffix = '_0' // デフォルトは sub_id=0（既存セルがあれば上書き）
    const isEditing = !!editingKey
    if (!isEditing) {
      const sameDayEntries = logEntries.filter(
        (e) => e.vendorId === targetVendorId && e.date === date
      )
      if (sameDayEntries.length > 0) {
        const maxSubId = sameDayEntries.reduce((m, e) => Math.max(m, e.subId), 0)
        const list = sameDayEntries
          .map((e) => `・ sub_id=${e.subId}: ${e.type === 'expense' ? '−' : ''}¥${e.amount.toLocaleString()}${e.dueDate ? ` (期限:${e.dueDate})` : ''}${e.note ? ` 「${e.note.slice(0, 20)}」` : ''}`)
          .join('\n')
        const answer = window.confirm(
          `【同日・同取引先の既存取引があります】\n` +
          `${date} ${targetVendorName} に既に ${sameDayEntries.length} 件登録されています:\n\n${list}\n\n` +
          `[OK] → 別件として追加（sub_id=${maxSubId + 1} で新規作成）\n` +
          `[キャンセル] → 既存 sub_id=0 を上書き`
        )
        if (answer) {
          subIdSuffix = '_new' // サーバー側で MAX(sub_id)+1 を採番
        } else {
          subIdSuffix = '_0' // 既存 sub_id=0 を上書き
        }
      }
    }

    // 編集モードでは editingKey から sub_id を引き継ぐ（フロントキーは末尾が subId）
    // 新規登録の場合は上で決めた subIdSuffix を付ける
    const newKey = isEditing
      ? `${targetVendorId}_${date}_${editingKey.split('_').pop()}`
      : `${targetVendorId}_${date}${subIdSuffix}`
    // キーが変わった場合（取引先 or 日付変更）は元セルを 0 にする
    const keyChanged = isEditing && editingKey !== newKey
    // 編集対象が確認済みだったか
    const editingEntry = isEditing ? logEntries.find((e) => e.key === editingKey) : null
    const wasConfirmed = !!editingEntry?.confirmedByName

    setSaving(true)
    try {
      const cellsBody: Record<string, number> = { [newKey]: amountNum }
      if (keyChanged) cellsBody[editingKey] = 0
      const saveBody: any = { cells: cellsBody }
      // メモ・支払期限・定期フラグは常に送る（空文字/nullで上書き = 削除も表現）
      saveBody.cellMeta = {
        [newKey]: {
          note: note.trim(),
          status: 'pending',
          dueDate: dueDate || null,
          isRecurring,
          targetMonth: targetMonth || null,
          departmentId: departmentId || null,
          itemId: itemId || null,
          // シークレットフラグ: 権限ある人のみサーバが反映（権限ない人が送ってもサーバ側で無視）
          isSecret,
        },
      }
      await api.cells.save(saveBody)

      const baseMsg = isEditing
        ? `${date} ${targetVendorName} ¥${amountNum.toLocaleString()} を更新`
        : `${date} ${targetVendorName} ¥${amountNum.toLocaleString()} を登録`
      toast.success(
        wasConfirmed ? `${baseMsg}（再確認が必要です）` : baseMsg,
        wasConfirmed ? 4000 : 2500
      )

      if (isEditing) {
        exitEdit()
      } else {
        resetForm()
      }
      if (addingVendor) {
        setAddingVendor(false)
        setNewVendorName('')
        await loadAll()
        setVendorId(targetVendorId)
      }
      await loadRecent()  // 履歴を再取得
    } catch (err) {
      toast.error(`${editingKey ? '更新' : '登録'}失敗: ${err instanceof Error ? err.message : ''}`)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* ヘッダー: 独立モード(収支登録のみのユーザー)時は名前+ログアウト付きのフル表示 */}
      {/* 通常モードではTopBarがユーザー情報を既に表示しているので省略しない */}
      {isStandalone ? (
        <header className="bg-white border-b border-slate-200 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ZaimuKun mood="happy" size={32} />
              <div>
                <h1 className="text-sm font-bold text-gray-900">収支登録</h1>
                <p className="text-[10px] text-gray-500">{user?.name} さん</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className="text-gray-500">本日登録 <strong className="text-gray-900 tabular-nums">{todayLogCount}</strong>件</span>
              <button
                onClick={logout}
                className="flex items-center gap-1 px-2 py-1 text-gray-600 hover:bg-gray-100 rounded transition"
              >
                <LogOut className="w-3 h-3" />
                ログアウト
              </button>
            </div>
          </div>
        </header>
      ) : null /* 通常モードでは TopBar がページ名を表示するのでここでは出さない */}

      <div className="max-w-7xl mx-auto px-4 py-3 space-y-3">
        {/* 編集モード時は画面右側にオーバーレイ（テーブル操作をガードしつつクリックでcloseも可） */}
        {editingKey && (
          <div
            className={`fixed inset-0 bg-black/20 z-30 transition-opacity duration-300 ease-out ${
              panelOpen ? 'opacity-100' : 'opacity-0'
            }`}
            onClick={exitEdit}
            aria-hidden="true"
          />
        )}

        {/* 通常モード時のサブヘッダー: 本日登録件数 + フォーム折りたたみボタン */}
        {!isStandalone && !editingKey && (
          <div className="flex items-center justify-end gap-3">
            <span className="text-xs text-gray-500">
              本日登録 <strong className="text-gray-900 tabular-nums">{todayLogCount}</strong>件
            </span>
            <button
              type="button"
              onClick={() => setFormCollapsed((v) => !v)}
              className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 border border-gray-200 rounded transition"
              title={formCollapsed ? 'フォームを展開' : 'フォームを折りたたむ'}
            >
              {formCollapsed ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
              {formCollapsed ? '入力フォームを開く' : '入力フォームを閉じる'}
            </button>
          </div>
        )}

        {/* 折りたたみ時の最小プレビュー（クリックで展開） */}
        {!editingKey && formCollapsed && (
          <button
            type="button"
            onClick={() => setFormCollapsed(false)}
            className="w-full flex items-center justify-between px-3 py-2 bg-white border border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-blue-50 hover:border-blue-400 transition"
            title="クリックで入力フォームを開く"
          >
            <span className="flex items-center gap-2">
              <ChevronDown className="w-4 h-4 text-gray-400" />
              <Plus className="w-4 h-4 text-blue-500" />
              新しい取引を登録（クリックで開く）
            </span>
            <span className="text-xs text-gray-400">折りたたみ中</span>
          </button>
        )}

        {/* 入力フォーム: 新規登録モードは上部、編集モードは右サイドピーク（ぬるっとスライドイン） */}
        {(editingKey || !formCollapsed) && (
        <div
          className={
            editingKey
              ? `fixed top-14 right-0 bottom-0 w-full sm:w-[580px] z-40 bg-white border-l border-amber-300 shadow-2xl overflow-y-auto p-6 space-y-4 transition-transform duration-300 ease-out will-change-transform ${
                  panelOpen ? 'translate-x-0' : 'translate-x-full'
                }`
              : 'bg-white rounded-lg shadow-sm border p-3 space-y-2 border-gray-200'
          }
        >
          {/* 編集モード時: サイドピーク内のヘッダー（対象取引の情報＋閉じるボタン） */}
          {editingKey && (
            <div className="flex items-center justify-between gap-2 pb-2 mb-2 border-b border-amber-200 sticky top-0 bg-white z-10">
              <div className="flex items-center gap-2 text-amber-900 min-w-0">
                <Edit2 className="w-4 h-4 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-xs font-medium text-amber-700">編集中</div>
                  <div className="text-sm font-bold truncate">{editingLabel}</div>
                </div>
              </div>
              <button
                type="button"
                onClick={exitEdit}
                className="flex items-center gap-1 px-2 py-1 text-xs bg-white hover:bg-gray-50 text-gray-600 border border-gray-300 rounded transition whitespace-nowrap flex-shrink-0"
                title="閉じる (Esc)"
              >
                <X className="w-3.5 h-3.5" />閉じる
              </button>
            </div>
          )}

          {/* 収入/支出 + 取引種別 を1段に統合 */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="grid grid-cols-2 gap-1.5 flex-1 min-w-[200px]">
              <button
                onClick={() => setType('income')}
                className={`flex items-center justify-center gap-1.5 py-1.5 rounded border transition text-sm ${
                  type === 'income'
                    ? 'bg-green-50 border-green-500 text-green-800 font-medium'
                    : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                }`}
              >
                <TrendingUp className="w-3.5 h-3.5" />
                収入
              </button>
              <button
                onClick={() => setType('expense')}
                className={`flex items-center justify-center gap-1.5 py-1.5 rounded border transition text-sm ${
                  type === 'expense'
                    ? 'bg-red-50 border-red-500 text-red-800 font-medium'
                    : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                }`}
              >
                <TrendingDown className="w-3.5 h-3.5" />
                支出
              </button>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <label className="text-[11px] font-medium text-gray-600 whitespace-nowrap flex items-center gap-1">
                {isRecurring
                  ? <Repeat className="w-3 h-3 text-blue-600" />
                  : <Zap className="w-3 h-3 text-slate-500" />}
                取引種別
              </label>
              <select
                value={isRecurring ? 'recurring' : 'onetime'}
                onChange={(e) => setIsRecurring(e.target.value === 'recurring')}
                disabled={saving}
                className={`w-56 px-2 py-1.5 border rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                  isRecurring
                    ? 'bg-blue-50 border-blue-300 text-blue-900'
                    : 'bg-white border-gray-300 text-gray-700'
                }`}
              >
                <option value="onetime">単発(今回限り)</option>
                <option value="recurring">定期(繰り返し・金額変動OK)</option>
              </select>
            </div>
            {/* シークレット取引チェックボックス（権限あるユーザーのみ表示） */}
            {(user?.role === 'admin' || !!user?.canViewSecret) && (
              <label
                className={`flex items-center gap-1.5 px-2 py-1.5 rounded border cursor-pointer text-xs flex-shrink-0 transition ${
                  isSecret
                    ? 'bg-purple-50 border-purple-300 text-purple-900'
                    : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
                title="オン: シークレット閲覧権限のないユーザーには金額が「***」表示され編集できません"
              >
                <input
                  type="checkbox"
                  checked={isSecret}
                  onChange={(e) => setIsSecret(e.target.checked)}
                  disabled={saving}
                  className="w-3.5 h-3.5 text-purple-600 border-purple-300 rounded"
                />
                <span className="font-medium whitespace-nowrap">🔒 シークレット</span>
              </label>
            )}
          </div>

          {/* カテゴリ＋取引先＋部門＋品目（デスクトップ: 2x2 グリッド） */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">

          {/* カテゴリ */}
          <div className="min-w-0">
            <label className="block text-[11px] font-medium text-gray-600 mb-0.5">カテゴリ</label>
            {!addingCategory ? (
              <div className="flex gap-1.5">
                <select
                  value={categoryId}
                  onChange={(e) => {
                    setCategoryId(e.target.value)
                    // カテゴリが変わったら取引先もリセット（編集中の初期復元は handleStartEdit で setCategoryId→setVendorId の順で上書き）
                    setVendorId('')
                    setAddingVendor(false)
                    setNewVendorName('')
                  }}
                  disabled={saving}
                  className="flex-1 min-w-0 px-2 py-1.5 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                >
                  {availableCategories.length === 0 ? (
                    <option value="">カテゴリがありません - 新規作成してください</option>
                  ) : (
                    <>
                      <option value="">選択してください</option>
                      {availableCategories.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </>
                  )}
                </select>
                <button
                  onClick={() => setAddingCategory(true)}
                  disabled={saving}
                  className="flex items-center justify-center w-7 h-7 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded border border-blue-200 transition flex-shrink-0"
                  title={`新規${type === 'income' ? '収入' : '支出'}カテゴリを作成`}
                  aria-label="新規カテゴリ"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <div className="flex gap-1.5">
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  onKeyDown={(e) => {
                    // Enter は無効化（漢字変換の確定で誤登録されるのを防ぐ。作成ボタンを押して登録）
                    if (e.key === 'Enter') e.preventDefault()
                    if (e.key === 'Escape') { setAddingCategory(false); setNewCategoryName('') }
                  }}
                  placeholder={`新規${type === 'income' ? '収入' : '支出'}カテゴリ名`}
                  disabled={creatingCategory}
                  autoFocus
                  className="flex-1 px-2 py-1.5 border border-blue-300 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                />
                <button
                  onClick={handleCreateCategory}
                  disabled={creatingCategory || !newCategoryName.trim()}
                  className="flex items-center gap-1 px-2 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded transition"
                >
                  {creatingCategory ? <Loader2 className="w-3 h-3 animate-spin" /> : '作成'}
                </button>
                <button
                  onClick={() => { setAddingCategory(false); setNewCategoryName('') }}
                  disabled={creatingCategory}
                  className="flex items-center gap-1 px-2 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 rounded transition"
                >
                  <ArrowLeft className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>

          {/* 取引先 */}
          <div className="min-w-0">
            <label className="block text-[11px] font-medium text-gray-600 mb-0.5">取引先</label>
            {!addingVendor ? (
              <div className="flex gap-1.5">
                <select
                  value={vendorId}
                  onChange={(e) => setVendorId(e.target.value)}
                  disabled={saving || availableVendors.length === 0}
                  className="flex-1 min-w-0 px-2 py-1.5 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                >
                  <option value="">選択してください</option>
                  {availableVendors.map((v) => (
                    <option key={v.id} value={v.id}>{v.name}</option>
                  ))}
                </select>
                <button
                  onClick={() => setAddingVendor(true)}
                  disabled={saving || !categoryId}
                  className="flex items-center justify-center w-7 h-7 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded border border-blue-200 transition flex-shrink-0 disabled:opacity-50"
                  title="新規取引先を作成"
                  aria-label="新規取引先"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <div className="flex gap-1.5">
                <input
                  type="text"
                  value={newVendorName}
                  onChange={(e) => setNewVendorName(e.target.value)}
                  onKeyDown={(e) => {
                    // Enter は無効化（漢字変換の確定で誤登録されるのを防ぐ。作成ボタンを押して登録）
                    if (e.key === 'Enter') e.preventDefault()
                    if (e.key === 'Escape') { setAddingVendor(false); setNewVendorName('') }
                  }}
                  placeholder="新規取引先名"
                  disabled={saving || creatingVendor}
                  autoFocus
                  className="flex-1 px-2 py-1.5 border border-blue-300 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                />
                <button
                  onClick={handleCreateVendor}
                  disabled={saving || creatingVendor || !newVendorName.trim()}
                  className="flex items-center gap-1 px-2 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded transition"
                >
                  {creatingVendor ? <Loader2 className="w-3 h-3 animate-spin" /> : '作成'}
                </button>
                <button
                  onClick={() => { setAddingVendor(false); setNewVendorName('') }}
                  disabled={saving || creatingVendor}
                  className="flex items-center gap-1 px-2 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 rounded transition"
                >
                  <ArrowLeft className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>
          {/* 部門（任意、階層パス表示、最大4階層） */}
          <div className="min-w-0">
            <label className="block text-[11px] font-medium text-gray-600 mb-0.5">
              部門
              <span className="ml-1 text-[10px] text-gray-400 font-normal">(任意)</span>
            </label>
            {!addingDepartment ? (
              <div className="flex gap-1.5">
                <select
                  value={departmentId}
                  onChange={(e) => setDepartmentId(e.target.value)}
                  disabled={saving}
                  className="flex-1 min-w-0 px-2 py-1.5 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                  title={departmentId ? getDepartmentPath(departmentId) : '部門を選択'}
                >
                  <option value="">— 部門なし —</option>
                  {departmentOptions.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.label}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => setAddingDepartment(true)}
                  disabled={saving}
                  className="flex items-center justify-center w-7 h-7 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded border border-blue-200 transition flex-shrink-0 disabled:opacity-50"
                  title="新規部門を作成（最上位階層）"
                  aria-label="新規部門"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <div className="flex gap-1.5">
                <input
                  type="text"
                  value={newDepartmentName}
                  onChange={(e) => setNewDepartmentName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.preventDefault()
                    if (e.key === 'Escape') { setAddingDepartment(false); setNewDepartmentName('') }
                  }}
                  placeholder="新規部門名"
                  disabled={creatingDepartment}
                  autoFocus
                  className="flex-1 min-w-0 px-2 py-1.5 border border-blue-400 rounded text-sm bg-blue-50 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={handleCreateDepartment}
                  disabled={creatingDepartment || !newDepartmentName.trim()}
                  className="px-2 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded transition whitespace-nowrap flex-shrink-0"
                >
                  作成
                </button>
                <button
                  type="button"
                  onClick={() => { setAddingDepartment(false); setNewDepartmentName('') }}
                  disabled={creatingDepartment}
                  className="px-1.5 py-1.5 text-gray-400 hover:text-gray-600 flex-shrink-0"
                  title="キャンセル"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
            {departmentId && !addingDepartment && (
              <div className="mt-0.5 text-[10px] text-gray-500 truncate" title={getDepartmentPath(departmentId)}>
                {getDepartmentPath(departmentId)}
              </div>
            )}
          </div>

          {/* 品目（任意、フラットマスタ、freee品目と同等） — 2x2 グリッドの4番目 */}
          <div className="min-w-0">
            <label className="block text-[11px] font-medium text-gray-600 mb-0.5">
              品目
              <span className="ml-1 text-[10px] text-gray-400 font-normal">(任意)</span>
            </label>
            {!addingItem ? (
              <div className="flex gap-1.5">
                <select
                  value={itemId}
                  onChange={(e) => setItemId(e.target.value)}
                  disabled={saving}
                  className="flex-1 min-w-0 px-2 py-1.5 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                >
                  <option value="">— 品目なし —</option>
                  {sortedItems.map((i) => (
                    <option key={i.id} value={i.id}>{i.name}</option>
                  ))}
                </select>
                <button
                  onClick={() => setAddingItem(true)}
                  disabled={saving}
                  className="flex items-center justify-center w-7 h-7 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded border border-blue-200 transition flex-shrink-0 disabled:opacity-50"
                  title="新規品目を作成"
                  aria-label="新規品目"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <div className="flex gap-1.5">
                <input
                  type="text"
                  value={newItemName}
                  onChange={(e) => setNewItemName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.preventDefault()
                    if (e.key === 'Escape') { setAddingItem(false); setNewItemName('') }
                  }}
                  placeholder="新規品目名"
                  disabled={creatingItem}
                  autoFocus
                  className="flex-1 min-w-0 px-2 py-1.5 border border-blue-400 rounded text-sm bg-blue-50 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={handleCreateItem}
                  disabled={creatingItem || !newItemName.trim()}
                  className="px-2 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded transition whitespace-nowrap flex-shrink-0"
                >
                  作成
                </button>
                <button
                  type="button"
                  onClick={() => { setAddingItem(false); setNewItemName('') }}
                  disabled={creatingItem}
                  className="px-1.5 py-1.5 text-gray-400 hover:text-gray-600 flex-shrink-0"
                  title="キャンセル"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>
          </div>{/* カテゴリ+取引先+部門+品目 grid end */}

          {/* 請求日・支払期限・対象月・金額（デスクトップは4列、モバイルは2列） */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div>
              <label className="block text-[11px] font-medium text-gray-600 mb-0.5">請求日</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                disabled={saving}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-600 mb-0.5">
                {dueDateLabel(type)}
                <span className="ml-1 text-[10px] text-gray-400 font-normal">(任意)</span>
              </label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                disabled={saving}
                min={date || undefined}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-600 mb-0.5">
                対象月
                <span className="ml-1 text-[10px] text-gray-400 font-normal">(何月分)</span>
              </label>
              <input
                type="month"
                value={targetMonth}
                onChange={(e) => setTargetMonth(e.target.value)}
                disabled={saving}
                placeholder="YYYY-MM"
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-600 mb-0.5">
                金額（税込・円）
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={amount}
                onChange={(e) => setAmount(e.target.value.replace(/[^\d,]/g, ''))}
                placeholder="0"
                disabled={saving}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-right font-medium tabular-nums bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
          </div>

          {/* メモ（独立行で広く取る） */}
          <div>
            <label className="block text-[11px] font-medium text-gray-600 mb-0.5">メモ（任意）</label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={saving}
              placeholder="備考・内訳など"
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </div>

          <button
            onClick={handleSave}
            disabled={saving || !amount || !date || (addingVendor ? !newVendorName.trim() : !vendorId)}
            className={`w-full flex items-center justify-center gap-1.5 py-2 text-white font-medium rounded transition text-sm ${
              editingKey
                ? 'bg-amber-600 hover:bg-amber-700 disabled:bg-amber-300'
                : 'bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300'
            }`}
          >
            {saving ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : editingKey ? (
              <Save className="w-3.5 h-3.5" />
            ) : (
              <Send className="w-3.5 h-3.5" />
            )}
            {saving ? (editingKey ? '更新中...' : '登録中...') : editingKey ? '更新する' : '登録する'}
          </button>

          {/* 編集モード専用: 削除ボタン（区切り線の下に配置、誤操作防止） */}
          {editingKey && (() => {
            const editingEntry = logEntries.find((e) => e.key === editingKey)
            if (!editingEntry) return null
            return (
              <div className="pt-4 mt-4 border-t border-gray-200">
                <div className="text-[10px] text-gray-400 mb-2">⚠ 取り消しはゴミ箱から可能（30日）</div>
                <button
                  type="button"
                  onClick={async () => {
                    await handleDelete(editingEntry)
                  }}
                  disabled={saving}
                  className="w-full flex items-center justify-center gap-1.5 py-2 text-xs text-red-700 hover:bg-red-50 border border-red-300 rounded transition disabled:opacity-50"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  この取引を削除する
                </button>
              </div>
            )
          })()}
        </div>
        )}

        {/* R-9 (2026-05-24): 登録履歴セクションを features/input/components/InputHistoryTable.tsx に分離 */}
        <InputHistoryTable
          logEntries={logEntries}
          filteredLogEntries={filteredLogEntries}
          loadingRecent={loadingRecent}
          aggregate={aggregate}
          todayLogCount={todayLogCount}
          commentCounts={commentCounts}
          itemMap={itemMap}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          typeFilter={typeFilter}
          setTypeFilter={setTypeFilter}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          recurringFilter={recurringFilter}
          setRecurringFilter={setRecurringFilter}
          periodDays={periodDays}
          setPeriodDays={setPeriodDays}
          toggleSort={toggleSort}
          sortIcon={sortIcon}
          confirmingKey={confirmingKey}
          deletingKey={deletingKey}
          editingKey={editingKey}
          loadRecent={loadRecent}
          onShowDuplicate={() => setShowDuplicateDialog(true)}
          onSelectCommentTarget={setCommentTarget}
          handleConfirm={handleConfirm}
          handleStartEdit={handleStartEdit}
        />

      </div>

      {/* コメントダイアログ */}
      {commentTarget && (
        <CommentDialog
          vendorId={commentTarget.vendorId}
          date={commentTarget.date}
          subId={commentTarget.subId}
          vendorName={commentTarget.vendorName}
          amount={commentTarget.amount}
          onClose={() => setCommentTarget(null)}
          onCountChanged={loadCommentCounts}
        />
      )}

      {/* 重複チェックダイアログ（features/input/components/DuplicateCheckDialog.tsx に分離） */}
      {showDuplicateDialog && (
        <DuplicateCheckDialog
          entries={logEntries}
          periodDays={periodDays}
          onClose={() => setShowDuplicateDialog(false)}
          onEdit={handleStartEdit}
          onDelete={handleDelete}
        />
      )}
    </div>
  )
}
